import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PieController,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Pie, Chart as ReactChart } from 'react-chartjs-2';

// Chart.js canvases are transparent by default; Google Charts always
// painted an opaque white background. Without this, the chart inherits
// whatever is behind it (e.g. `.chartMobile`'s dark loading skeleton).
const whiteBackgroundPlugin = {
  id: 'whiteBackground',
  beforeDraw: (chart) => {
    const { ctx } = chart;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  },
};

ChartJS.register(
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PieController,
  PointElement,
  Title,
  Tooltip,
  whiteBackgroundPlugin,
  ChartDataLabels
);

// Chart.js's default text color is too light against a white background.
ChartJS.defaults.color = '#070707';

// Font size for every legend (native canvas one and the custom HTML one).
const LEGEND_FONT_SIZE = 14;

// Same breakpoint `.chartMobile` already uses elsewhere in the project.
const MOBILE_BREAKPOINT = 737;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return isMobile;
}

const DEFAULT_COLORS = [
  '#3366cc',
  '#dc3912',
  '#ff9900',
  '#109618',
  '#990099',
  '#0099c6',
  '#dd4477',
  '#66aa00',
  '#b82e2e',
  '#316395',
];

function colorAt(colors, index) {
  return colors[index % colors.length];
}

function toLegendPosition(position) {
  // Google Charts has 'labeled' (labels drawn next to each pie slice),
  // with no Chart.js equivalent. Falls back to 'right', Google's default.
  if (!position || position === 'labeled') return 'right';
  return position;
}

function buildTitlePlugin(options) {
  return options.title
    ? { display: true, text: options.title, font: { size: 14 } }
    : { display: false };
}

// Slices below this don't show a %. datalabels' `display: 'auto'` only
// keeps the text from spilling outside the slice, but neighboring small
// slices could still fit it technically and end up cramped together —
// Google Charts hid the label in that case too, so an explicit floor is
// used instead of relying on 'auto' alone.
const MIN_PERCENT_FOR_LABEL = 5;

function percentOf(value, values) {
  const total = values.reduce((sum, v) => sum + (Number(v) || 0), 0);
  return total ? (Number(value) / total) * 100 : 0;
}

// Some screens already build the label with the count baked into the name
// — "IOTs (352)" or "Marco Papa - 42" — without this the tooltip would
// duplicate it, e.g. "Marco Papa - 42: 42 (61%)".
function stripCountSuffix(label) {
  return String(label)
    .replace(/\s*\(\d+\)\s*$/, '')
    .replace(/\s*-\s*\d+\s*$/, '');
}

// When there's no real data, the API returns a placeholder like
// "Nenhum registro - 0" with a fake value of 100 (just to draw a full
// circle). "100 (100%)" wouldn't mean anything useful there — just show
// the placeholder text as-is.
function isEmptyPlaceholder(label) {
  return /nenhum registro/i.test(String(label));
}

function buildPieTooltip() {
  return {
    callbacks: {
      label: (context) => {
        if (isEmptyPlaceholder(context.label)) return context.label;
        const pct = percentOf(context.parsed, context.dataset.data);
        const name = stripCountSuffix(context.label);
        return `${name}: ${context.parsed} (${Math.round(pct)}%)`;
      },
    },
  };
}

function buildPieDataLabels() {
  return {
    display: (context) => {
      const pct = percentOf(
        context.dataset.data[context.dataIndex],
        context.dataset.data
      );
      return pct >= MIN_PERCENT_FOR_LABEL ? 'auto' : false;
    },
    color: '#fff',
    font: { weight: 'bold', size: 13 },
    textShadowBlur: 3,
    textShadowColor: 'rgba(0, 0, 0, 0.45)',
    formatter: (value, context) => {
      const pct = percentOf(value, context.dataset.data);
      return `${pct.toFixed(1)}%`;
    },
  };
}

// Canvas-drawn legend (used when it sits at the top/bottom) — Chart.js
// text has no DOM element, so this can't be a CSS/Bootstrap class; the
// font size has to be set through Chart.js's own options.
function buildLegendPlugin(options) {
  return {
    display: true,
    position: toLegendPosition(options.legend?.position),
    labels: { font: { size: LEGEND_FONT_SIZE } },
  };
}

function buildAxisTicks(axisOptions) {
  // Without this Chart.js picks a "nice" scale step that can land on a
  // fractional value (e.g. 0, 2.5, 5, 7.5). The numeric axes here are
  // always counts/scores — precision:0 forces the computed step to be
  // an integer.
  const ticks = { precision: 0 };
  if (axisOptions?.format && String(axisOptions.format).includes('%')) {
    ticks.callback = (value) => `${value}%`;
  }
  if (axisOptions?.gridlines?.count) {
    // NOT `ticks.count`: that forces exactly N ticks by dividing the
    // range evenly, which usually lands on non-integer steps (e.g. a
    // 0-6 range split into 10 ticks steps by 0.666...) and Chart.js then
    // renders those with excessive floating-point decimals. `maxTicksLimit`
    // keeps Chart.js's own "nice" integer step selection and just caps
    // how many of those clean ticks are drawn — matching Google Charts'
    // `gridlines.count`, which was always an approximate cap, not exact.
    ticks.maxTicksLimit = axisOptions.gridlines.count;
  }
  return ticks;
}

function buildScales(options) {
  return {
    x: {
      stacked: !!options.isStacked,
      title: options.hAxis?.title
        ? { display: true, text: options.hAxis.title }
        : undefined,
    },
    y: {
      stacked: !!options.isStacked,
      min: options.vAxis?.viewWindow?.min,
      max: options.vAxis?.viewWindow?.max,
      title: options.vAxis?.title
        ? { display: true, text: options.vAxis.title }
        : undefined,
      ticks: buildAxisTicks(options.vAxis),
    },
  };
}

function parsePercent(value) {
  const match = /^(\d+(?:\.\d+)?)%$/.exec(String(value || '').trim());
  return match ? Number(match[1]) / 100 : undefined;
}

// Some components passed width/height like '108%' to compensate for an
// inner margin Google Charts left inside its own container. Chart.js's
// canvas already fills 100% of the real container — letting >100% through
// makes it spill past the Bootstrap column and overlap its neighbor.
function clampSize(value) {
  const pct = parsePercent(value);
  if (pct !== undefined) return pct > 1 ? '100%' : value;
  return value;
}

function toPieChartData(rows, options) {
  const body = rows.slice(1);
  const labels = body.map((row) => row[0]);
  const values = body.map((row) => Number(row[1]) || 0);
  const colors = options.colors?.length ? options.colors : DEFAULT_COLORS;

  return {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: labels.map((_, index) => colorAt(colors, index)),
      },
    ],
  };
}

function toCartesianChartData(rows, options, defaultType) {
  const [header, ...body] = rows;
  const labels = body.map((row) => row[0]);
  const seriesNames = header.slice(1);
  const colors = options.colors?.length ? options.colors : DEFAULT_COLORS;
  const groupWidth = parsePercent(options.bar?.groupWidth);

  const datasets = seriesNames.map((name, index) => {
    const seriesOverride = options.series?.[index] || {};
    const type = seriesOverride.type || defaultType;
    const color = seriesOverride.color || colorAt(colors, index);

    const dataset = {
      label: name,
      data: body.map((row) => Number(row[index + 1]) || 0),
      type,
      borderColor: color,
      backgroundColor: color,
    };

    if (type === 'line') {
      dataset.pointRadius = options.pointSize ?? 3;
      dataset.fill = false;
      dataset.tension = 0;
    } else if (groupWidth) {
      dataset.categoryPercentage = groupWidth;
    }

    return dataset;
  });

  return { labels, datasets };
}

// HTML legend with its own scrolling, real text that wraps onto a second
// line when it's too long, and click-to-toggle. Google Charts paginates
// the legend (▲▼ arrows) when it doesn't fit the chart's height, and
// wraps long labels; Chart.js's canvas-drawn legend does neither — with
// many categories or long names it either spills over the rest of the
// page or gets clipped by the canvas edge. This replaces the native
// legend only when it sits to the side (left/right), where that happens.
function SideLegend({ chart, labels, colors, isPie, horizontal = false }) {
  const [, bump] = useState(0);

  if (!chart) return null;

  const isHidden = (index) => {
    if (isPie) return !chart.getDataVisibility(index);
    return !!chart.getDatasetMeta(index).hidden;
  };

  const toggle = (index) => {
    if (isPie) {
      chart.toggleDataVisibility(index);
    } else {
      const meta = chart.getDatasetMeta(index);
      meta.hidden = !meta.hidden;
    }
    chart.update();
    bump((n) => n + 1);
  };

  return (
    <ul
      style={{
        listStyle: 'none',
        margin: 0,
        padding: '0 4px',
        display: horizontal ? 'flex' : 'block',
        flexWrap: horizontal ? 'wrap' : undefined,
        justifyContent: horizontal ? 'center' : undefined,
        gap: horizontal ? '4px 16px' : undefined,
        overflowY: 'auto',
        height: '100%',
        fontSize: LEGEND_FONT_SIZE,
        color: '#070707',
        boxSizing: 'border-box',
      }}
    >
      {labels.map((label, index) => {
        const hidden = isHidden(index);
        return (
          <li
            key={index}
            onClick={() => toggle(index)}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 6,
              cursor: 'pointer',
              padding: '3px 0',
              opacity: hidden ? 0.4 : 1,
              textDecoration: hidden ? 'line-through' : 'none',
            }}
          >
            <span
              style={{
                width: 12,
                height: 12,
                marginTop: 3,
                flexShrink: 0,
                borderRadius: 2,
                background: colorAt(colors, index),
              }}
            />
            <span style={{ wordBreak: 'break-word' }}>{label}</span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Drop-in replacement for `react-google-charts` backed by Chart.js, with
 * the same interface (chartType/data/options in Google Charts' 2D array
 * format) so components using it don't need to be rewritten. Only covers
 * the chart types/options actually used in this project (PieChart,
 * ComboChart) — not a full clone of the Google Charts API.
 */
export default function GoogleChartsCompat({
  chartType,
  data,
  options = {},
  width,
  height,
}) {
  const rows = Array.isArray(data) ? data : [];
  const style = {
    width: clampSize(width) || '100%',
    height: clampSize(height) || '300px',
  };

  const isPie = chartType === 'PieChart';
  const legendPosition = toLegendPosition(options.legend?.position);
  const wantsSidePosition =
    legendPosition === 'right' || legendPosition === 'left';
  const isMobile = useIsMobile();
  // On phones a side-by-side pie + legend has no room to breathe — Google
  // Charts didn't handle this either, so instead of copying it, the legend
  // moves below the chart on narrow screens (pie only; bar/line legends
  // already wrap fine at the bottom).
  const stackLegendBelow = isPie && wantsSidePosition && isMobile;
  const useSideLegend = isPie
    ? wantsSidePosition && !isMobile
    : wantsSidePosition;

  const chartRef = useRef(null);
  const [chartInstance, setChartInstance] = useState(null);
  const setChartRef = (instance) => {
    chartRef.current = instance;
    if (instance && instance !== chartInstance) setChartInstance(instance);
  };

  const hasCustomLegend = useSideLegend || stackLegendBelow;

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: buildTitlePlugin(options),
        legend: hasCustomLegend
          ? { display: false }
          : buildLegendPlugin(options),
        datalabels: isPie ? buildPieDataLabels() : { display: false },
        tooltip: isPie ? buildPieTooltip() : undefined,
      },
      scales: isPie ? undefined : buildScales(options),
    }),
    [JSON.stringify(options), isPie, hasCustomLegend]
  );

  if (rows.length < 2) {
    return <div style={style} />;
  }

  const chartData = isPie
    ? toPieChartData(rows, options)
    : toCartesianChartData(
        rows,
        options,
        chartType === 'LineChart' ? 'line' : 'bar'
      );

  const renderChart = (ref) =>
    isPie ? (
      <Pie ref={ref} data={chartData} options={chartOptions} />
    ) : (
      <ReactChart
        ref={ref}
        type={chartType === 'LineChart' ? 'line' : 'bar'}
        data={chartData}
        options={chartOptions}
      />
    );

  if (stackLegendBelow) {
    // Same content as the desktop side-legend layout, just stacked instead
    // of side-by-side, and capped shorter so it doesn't dominate the page
    // on a phone screen.
    return (
      <div
        style={{
          ...style,
          height: 300,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ flex: '1 1 auto', minHeight: 0 }}>
          {renderChart(setChartRef)}
        </div>
        <div style={{ height: 110, flexShrink: 0 }}>
          <SideLegend
            chart={chartInstance}
            labels={chartData.labels}
            colors={chartData.datasets[0].backgroundColor}
            isPie
            horizontal
          />
        </div>
      </div>
    );
  }

  // Below these widths the chart becomes unreadable (cramped slices/bars
  // and labels). Instead of keeping it shrinking on mobile, lock a
  // minimum width and let it scroll horizontally — the same pattern
  // `.chartMobile` already uses elsewhere in the project.
  if (!useSideLegend) {
    const minWidth = isPie ? 260 : 280;
    return (
      <div style={{ ...style, overflowX: 'auto' }}>
        <div style={{ minWidth, height: '100%' }}>{renderChart(null)}</div>
      </div>
    );
  }

  const legendLabels = isPie
    ? chartData.labels
    : chartData.datasets.map((d) => d.label);
  const legendColors = isPie
    ? chartData.datasets[0].backgroundColor
    : chartData.datasets.map((d) => d.backgroundColor);

  return (
    <div style={{ ...style, overflowX: 'auto' }}>
      <div style={{ display: 'flex', minWidth: 420, height: '100%' }}>
        <div style={{ flex: '1 1 auto', minWidth: 0, height: '100%' }}>
          {renderChart(setChartRef)}
        </div>
        <div style={{ width: 200, flexShrink: 0, height: '100%' }}>
          <SideLegend
            chart={chartInstance}
            labels={legendLabels}
            colors={legendColors}
            isPie={isPie}
          />
        </div>
      </div>
    </div>
  );
}
